import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "What Cybersecurity Actually Means".
 *
 * The lesson argues that perfect security is easy and useless, and that
 * the real work is finding the sensible point between safe and usable.
 * That argument is impossible to feel by reading it, so here it is as a
 * budget and two meters.
 *
 * Pick controls for a school. Each one buys protection and costs
 * friction, and three of them are deliberately terrible value - lots of
 * friction, very little protection - because a learner who has never
 * been tempted by one will not recognise it in the wild.
 *
 * Then the morning after. Three incidents play out against whatever was
 * actually bought, and a fourth appears if the friction went too high:
 * staff working around their own security, which is the failure nobody
 * expects and everybody causes.
 */

const STORE_KEY = 'cybercub.lab.trade-offs';

const BUDGET = 10;
const TARGET_PROTECTION = 70;
const FRICTION_LIMIT = 60;

/*
 * protect: how much harder this makes a real attack.
 * friction: how much it costs the people who work here, every day.
 * cost: the budget, standing in for money and staff time together.
 */
const CONTROLS = [
  {
    id: 'cctvpw',
    icon: '📹',
    name: 'Change the default password on the CCTV box',
    note: 'It is still the one printed in the manual.',
    protect: 10, friction: 1, cost: 0,
  },
  {
    id: 'mfa',
    icon: '🔐',
    name: 'Two-step sign-in for every staff account',
    note: 'A code or a tap as well as a password.',
    protect: 22, friction: 12, cost: 2,
  },
  {
    id: 'backup',
    icon: '💾',
    name: 'Nightly backups, kept offline',
    note: 'Offline matters. Ransomware encrypts anything it can reach.',
    protect: 20, friction: 2, cost: 3,
  },
  {
    id: 'patch',
    icon: '🩹',
    name: 'Updates installed weekly on every machine',
    note: 'Closes the holes that are already public.',
    protect: 18, friction: 4, cost: 3,
  },
  {
    id: 'passwords',
    icon: '🔑',
    name: 'Passphrases and a password manager',
    note: 'Three random words beat one clever substitution.',
    protect: 16, friction: 6, cost: 1,
  },
  {
    id: 'leastpriv',
    icon: '👤',
    name: 'Teachers on standard accounts, not administrator',
    note: 'Anything that runs as them can only do what they can.',
    protect: 15, friction: 10, cost: 1,
  },
  {
    id: 'training',
    icon: '🎣',
    name: 'Termly phishing practice for staff',
    note: 'Not a lecture — actual practice messages.',
    protect: 14, friction: 8, cost: 2,
  },
  {
    id: 'lockout',
    icon: '⏱️',
    name: 'Lock every screen after 30 seconds',
    note: 'Sounds strict. Think about a teacher mid-lesson.',
    protect: 6, friction: 22, cost: 0,
  },
  {
    id: 'banusb',
    icon: '🚫',
    name: 'Ban all USB sticks, no exceptions',
    note: 'Including the one the exam officer needs on Thursday.',
    protect: 8, friction: 25, cost: 1,
  },
  {
    id: 'vpnall',
    icon: '🐌',
    name: 'Force every click through a slow VPN',
    note: 'Every page, every video, every lesson.',
    protect: 9, friction: 28, cost: 3,
  },
];

/* ------------------------------------------------------------------ */
/* the morning after                                                  */
/* ------------------------------------------------------------------ */

/*
 * Each incident is judged against what was actually bought. `stoppedBy`
 * is any one of those controls; `softenedBy` does not prevent it but
 * changes how bad it gets.
 */
const INCIDENTS = [
  {
    id: 'phish',
    icon: '📧',
    title: 'A message to the office manager',
    setup:
      '"Your mailbox is full — sign in here to keep receiving messages." The link goes to a page that looks exactly like the school login.',
    stoppedBy: ['training'],
    softenedBy: ['mfa'],
    stopped: 'She had seen three practice ones this term, checked the address it really came from, and deleted it.',
    softened:
      'She typed her password into it. The attacker had the password and still could not get in — the second step asked for a code from her phone, and she had not been asked for one.',
    fails:
      'She typed her password into it, and by lunchtime somebody else was reading the school’s email. Nothing on the machine was broken. Nothing had to be.',
  },
  {
    id: 'ransom',
    icon: '🔒',
    title: 'A laptop in the staff room',
    setup:
      'It has not been updated since March. The hole it is missing was fixed — and published — seven months ago.',
    stoppedBy: ['patch'],
    softenedBy: ['backup'],
    stopped: 'The update went on in September. The attack tried the hole, found it closed, and moved on.',
    softened:
      'Everything on the shared drive was encrypted by Tuesday morning. It was restored from Monday night’s backup by the afternoon, and nobody paid anybody anything.',
    fails:
      'Everything on the shared drive was encrypted, including the only copy of this year’s coursework. There was nothing to restore from.',
  },
  {
    id: 'cctv',
    icon: '📹',
    title: 'A researcher gets in touch',
    setup:
      'She scans the internet for cameras with factory passwords. She has found the school’s, and she is telling you rather than anybody else.',
    stoppedBy: ['cctvpw'],
    stopped: 'She found the camera, tried the factory password, and it had been changed. She moved on.',
    fails:
      'She could see the corridor outside Year 7. So could anybody else who looked, and changing that password would have cost nothing and taken four minutes.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function TradeOffsLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('build');
  const [chosen, setChosen] = useState(saved.chosen ?? []);
  const [locked, setLocked] = useState(saved.locked ?? false);

  /* Committing the budget is not the lesson - the morning after is. */
  useFinished('/lab/trade-offs', locked && tab === 'after');

  useEffect(() => { document.title = 'The trade-off desk · CyberCub Lab'; }, []);
  useEffect(() => { save({ chosen, locked }); }, [chosen, locked]);

  const picked = CONTROLS.filter((c) => chosen.includes(c.id));
  const spent = picked.reduce((n, c) => n + c.cost, 0);
  const protection = Math.min(100, picked.reduce((n, c) => n + c.protect, 0));
  const friction = Math.min(100, picked.reduce((n, c) => n + c.friction, 0));

  const left = BUDGET - spent;
  const safeEnough = protection >= TARGET_PROTECTION;
  const workable = friction <= FRICTION_LIMIT;
  const ready = safeEnough && workable;

  const toggle = (c) => {
    if (locked) return;
    setChosen((prev) =>
      prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id],
    );
  };

  const has = (id) => chosen.includes(id);

  const verdict = (inc) => {
    if (inc.stoppedBy.some(has)) return 'stopped';
    if (inc.softenedBy?.some(has)) return 'softened';
    return 'failed';
  };

  const TABS = [
    { id: 'build', n: 1, label: 'Build the defence', locked: false },
    { id: 'after', n: 2, label: 'The morning after', locked: !locked },
  ];

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>The trade-off desk</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 1</span>
          </div>
        </div>
        <Link to="/learn/lesson/eh1l1-what-cybersecurity-means" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. build the defence ---------------- */}
      {tab === 'build' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Build the defence</h2>
              <p className="lab-lead">
                Hillside School has asked you what to do. You have {BUDGET} points of money and staff
                time. Get protection to {TARGET_PROTECTION} without pushing friction past{' '}
                {FRICTION_LIMIT} — because past that, people start working around you.
              </p>
            </div>
            <span className="pill">{left} left</span>
          </div>

          <Tutor
            avatar="cub-sky"
            say={
              ready
                ? 'That will hold, and people will still be able to teach. Lock it in and find out what Monday looks like.'
                : !safeEnough
                  ? 'Not enough protection yet. Look for the cheap ones first — there is something on that list that costs nothing at all and closes a real hole.'
                  : 'Protection is fine, but the friction is too high. A control people find unbearable gets switched off, written down or worked around, and then you have the cost without the protection.'
            }
          />

          <div className="meters">
            <div className="meter">
              <div className="meter-head">
                <span>Protection</span>
                <strong className={safeEnough ? 'is-good' : ''}>{protection} / {TARGET_PROTECTION}</strong>
              </div>
              <div className="meter-track">
                <i className={`meter-fill is-protect ${safeEnough ? 'is-good' : ''}`} style={{ width: `${protection}%` }} />
                <span className="meter-mark" style={{ left: `${TARGET_PROTECTION}%` }} />
              </div>
            </div>
            <div className="meter">
              <div className="meter-head">
                <span>Friction</span>
                <strong className={workable ? '' : 'is-bad'}>{friction} / {FRICTION_LIMIT}</strong>
              </div>
              <div className="meter-track">
                <i className={`meter-fill is-friction ${workable ? '' : 'is-bad'}`} style={{ width: `${friction}%` }} />
                <span className="meter-mark" style={{ left: `${FRICTION_LIMIT}%` }} />
              </div>
            </div>
          </div>

          <ul className="control-list">
            {CONTROLS.map((c) => {
              const on = has(c.id);
              const tooDear = !on && c.cost > left;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`control ${on ? 'is-on' : ''}`}
                    onClick={() => toggle(c)}
                    disabled={locked || tooDear}
                    aria-pressed={on}
                  >
                    <span className="control-icon" aria-hidden="true">{c.icon}</span>
                    <span className="control-body">
                      <strong>{c.name}</strong>
                      <em>{c.note}</em>
                    </span>
                    <span className="control-cost">
                      <b>{c.cost === 0 ? 'free' : `${c.cost} pt${c.cost > 1 ? 's' : ''}`}</b>
                      {tooDear && <i>not enough left</i>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {!locked && (
            <div className={`lab-done ${ready ? '' : 'is-wrong'}`}>
              <div>
                <strong>{ready ? 'That will do it.' : 'Not there yet.'}</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  {ready
                    ? 'Enough protection to matter, little enough friction that people will actually live with it.'
                    : 'You can lock it in anyway — plenty of real schools have. You will see what it costs.'}
                </p>
              </div>
              <button type="button" className="btn btn-sm" onClick={() => { setLocked(true); setTab('after'); }}>
                Lock it in →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. the morning after ---------------- */}
      {tab === 'after' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>The morning after</h2>
              <p className="lab-lead">
                It is Monday. Three things happen, and how each one ends depends entirely on what you
                bought.
              </p>
            </div>
            <div className="row gap-sm">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => { setLocked(false); setTab('build'); }}
              >
                ← Change the plan
              </button>
              <span className="pill">
                {INCIDENTS.filter((i) => verdict(i) === 'stopped').length} / {INCIDENTS.length} stopped
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              friction > FRICTION_LIMIT
                ? 'Look at the last one. Nobody attacked it — the staff did that themselves, because what you asked of them was more than the job allowed. That is a security failure, and it is one you caused.'
                : INCIDENTS.every((i) => verdict(i) === 'stopped')
                  ? 'All three, and the school still worked on Monday. That is the whole job: not the most security possible, the most that people will actually live with.'
                  : 'Read what happened rather than the score. Every one of these had a control that would have changed the ending, and every one of those controls cost something.'
            }
          />

          {INCIDENTS.map((inc) => {
            const v = verdict(inc);
            return (
              <div key={inc.id} className={`lab-case is-${v === 'stopped' ? 'right' : v === 'softened' ? 'part' : 'wrong'}`}>
                <span className="lab-case-n" aria-hidden="true">{inc.icon}</span>
                <div className="grow">
                  <p><strong>{inc.title}</strong></p>
                  <p className="case-setup">{inc.setup}</p>
                  <p className={`lab-feedback ${v === 'failed' ? 'is-wrong' : 'is-right'}`}>
                    {v === 'stopped' && `✓ Stopped. ${inc.stopped}`}
                    {v === 'softened' && `≈ It landed, and it did not matter much. ${inc.softened}`}
                    {v === 'failed' && `✗ ${inc.fails}`}
                  </p>
                </div>
              </div>
            );
          })}

          {friction > FRICTION_LIMIT && (
            <div className="lab-case is-wrong">
              <span className="lab-case-n" aria-hidden="true">🗒️</span>
              <div className="grow">
                <p><strong>Nobody attacked this one</strong></p>
                <p className="case-setup">
                  Friction finished at {friction}, and the people who work here are not villains —
                  they are busy.
                </p>
                <p className="lab-feedback is-wrong">
                  ✗ Two of the office passwords are now on a sticky note under a keyboard, because
                  typing them forty times a day was not survivable. The screen lock was turned off by
                  a teacher who was tired of it locking mid-sentence. You have paid the full price of
                  those controls and are no longer getting the protection.
                </p>
              </div>
            </div>
          )}

          <div className="lab-done">
            <div>
              <strong>
                {protection >= TARGET_PROTECTION && friction <= FRICTION_LIMIT
                  ? 'This is what the job actually is.'
                  : 'Worth another go.'}
              </strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                There was never a right answer here, only cheaper and dearer ways to be wrong. The
                free one closed a real hole; the three expensive ones bought almost nothing and cost
                everybody all day. Telling those apart is most of security.
              </p>
            </div>
            <Link to="/learn/lesson/eh1l1-what-cybersecurity-means" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
